import { FC, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import {
	Box,
	Button,
	HStack,
	Modal,
	ModalBody,
	ModalCloseButton,
	ModalContent,
	ModalFooter,
	ModalHeader,
	ModalOverlay,
	Text,
	VStack,
} from '@bonusly/design-system';

import SpookyLogoImage from './fall/images/logo-dark.png';
import HauntedHouseSound from './fall/sounds/HauntedHouse.mp3';

interface SeasonalThemeModalProps {
	isOpen: boolean;
	onClose: () => void;
	onEnable: () => void;
}

/**
 * Modal that displays when users click to enable the seasonal theme
 * Shows fun information about the Halloween/Fall theme features
 */
export const SeasonalThemeModal: FC<SeasonalThemeModalProps> = ({ isOpen, onClose, onEnable }) => {
	const { t } = useTranslation('components');
	const audioRef = useRef<HTMLAudioElement | null>(null);

	// Play sound when modal opens, stop when it closes
	useEffect(() => {
		if (isOpen) {
			// Create and play audio when modal opens
			audioRef.current = new Audio(HauntedHouseSound);
			audioRef.current.volume = 0.3; // Set volume to 30% to not be too loud
			audioRef.current.play().catch((error) => {
				console.warn('Failed to play haunted house sound:', error);
			});
		} else {
			// Stop and cleanup audio when modal closes
			if (audioRef.current) {
				audioRef.current.pause();
				audioRef.current.currentTime = 0;
				audioRef.current = null;
			}
		}

		// Cleanup on unmount
		return () => {
			if (audioRef.current) {
				audioRef.current.pause();
				audioRef.current = null;
			}
		};
	}, [isOpen]);

	const handleEnable = () => {
		onEnable();
		onClose();
	};

	return (
		<Modal isOpen={isOpen} onClose={onClose} size="xl" isCentered>
			<ModalOverlay bg="blackAlpha.800" backdropFilter="blur(4px)" />
			<ModalContent bg="black" borderRadius="2xl" boxShadow="0 20px 60px rgba(255, 107, 53, 0.5)" border="3px solid" borderColor="orange.500" overflow="hidden">
				{/* Decorative elements */}
				<Box position="absolute" top="10px" left="20px" fontSize="40px" opacity={0.2} transform="rotate(-15deg)">
					🎃
				</Box>
				<Box position="absolute" top="30px" right="30px" fontSize="30px" opacity={0.15} transform="rotate(15deg)">
					🦇
				</Box>
				<Box position="absolute" bottom="40px" left="30px" fontSize="25px" opacity={0.15} transform="rotate(-10deg)">
					👻
				</Box>
				<Box position="absolute" top="60px" left="60%" fontSize="20px" opacity={0.1} transform="rotate(25deg)">
					🍂
				</Box>

				<ModalHeader pt={12} pb={6} px={8} position="relative" zIndex={1}>
					<VStack spacing={4} align="center">
						<Box
							as="img"
							src={SpookyLogoImage}
							alt="Spooky Bonusly Logo"
							maxW="450px"
							w="100%"
							h="auto"
							filter="drop-shadow(0 4px 20px rgba(255, 107, 53, 0.6))"
						/>
					</VStack>
					<ModalCloseButton color="orange.400" _hover={{ bg: 'whiteAlpha.200', color: 'orange.300' }} top={4} right={4} size="lg" />
				</ModalHeader>

				<ModalBody px={8} pb={6} position="relative" zIndex={1}>
					<VStack spacing={6} align="stretch">
						<Text fontSize="lg" color="orange.200" textAlign="center" lineHeight="tall" fontWeight="medium">
							{t('seasonal_theme.modal.subtitle')}
						</Text>

						<VStack spacing={4} align="stretch">
							<HStack spacing={3} align="start" bg="whiteAlpha.50" p={3} borderRadius="md" border="1px solid" borderColor="whiteAlpha.100">
								<Text fontSize="xl" flexShrink={0}>
									🍂
								</Text>
								<Text fontSize="sm" color="whiteAlpha.900" lineHeight="tall">
									{t('seasonal_theme.modal.feature_1')}
								</Text>
							</HStack>

							<HStack spacing={3} align="start" bg="whiteAlpha.50" p={3} borderRadius="md" border="1px solid" borderColor="whiteAlpha.100">
								<Text fontSize="xl" flexShrink={0}>
									🎨
								</Text>
								<Text fontSize="sm" color="whiteAlpha.900" lineHeight="tall">
									{t('seasonal_theme.modal.feature_2')}
								</Text>
							</HStack>

							<HStack spacing={3} align="start" bg="whiteAlpha.50" p={3} borderRadius="md" border="1px solid" borderColor="whiteAlpha.100">
								<Text fontSize="xl" flexShrink={0}>
									✨
								</Text>
								<Text fontSize="sm" color="whiteAlpha.900" lineHeight="tall">
									{t('seasonal_theme.modal.feature_3')}
								</Text>
							</HStack>
						</VStack>

						<Box bg="orange.900" borderRadius="lg" p={4} border="1px solid" borderColor="orange.600" mt={2}>
							<Text fontSize="xs" color="orange.200" textAlign="center" fontStyle="italic">
								{t('seasonal_theme.modal.end_date_notice')}
							</Text>
						</Box>
					</VStack>
				</ModalBody>

				<ModalFooter px={8} pb={8} pt={4} position="relative" zIndex={1} flexDirection="column" gap={3}>
					<Button
						onClick={handleEnable}
						size="lg"
						width="full"
						bg="orange.600"
						color="white"
						fontWeight="bold"
						fontSize="lg"
						_hover={{
							bg: 'orange.500',
							transform: 'translateY(-2px)',
							boxShadow: '0 8px 25px rgba(255, 107, 53, 0.6)',
						}}
						_active={{
							bg: 'orange.700',
							transform: 'translateY(0)',
						}}
						transition="all 0.2s"
						boxShadow="0 4px 15px rgba(255, 107, 53, 0.4)"
					>
						{t('seasonal_theme.modal.enable_button')} 🎃
					</Button>

					<Button
						onClick={onClose}
						size="md"
						width="full"
						variant="ghost"
						color="orange.300"
						_hover={{
							bg: 'whiteAlpha.100',
							color: 'orange.200',
						}}
					>
						{t('seasonal_theme.modal.cancel_button')}
					</Button>
				</ModalFooter>
			</ModalContent>
		</Modal>
	);
};
